import {
  type FrontendAssortmentDimensionAttributeAggregation,
  GEOGRAPHIC_DIMENSION_ATTRIBUTES,
  type GeographicDimensionAttribute,
  type InSeasonAggregation,
  type InSeasonAggregationItemDimension,
  type PreSeasonAggregation,
  type PreSeasonAggregationItemDimension,
  PRODUCT_DIMENSION_ATTRIBUTES,
  type ProductDimensionAttribute,
} from '../types';

export const transformDimensionAttributes = (
  dimensionAttributes: FrontendAssortmentDimensionAttributeAggregation,
): Array<PreSeasonAggregation | InSeasonAggregation> => {
  const [productDimension, geographicDimension] = dimensionAttributes;
  const result: Array<PreSeasonAggregation | InSeasonAggregation> = [];

  if (productDimension !== 'all_products') {
    result.push(productDimension);
  }

  if (geographicDimension !== 'all_locations') {
    result.push(geographicDimension);
  }

  return result;
};

const isProductDimensionAttribute = (
  attribute: ProductDimensionAttribute | GeographicDimensionAttribute,
): attribute is ProductDimensionAttribute => {
  return PRODUCT_DIMENSION_ATTRIBUTES.includes(
    attribute as ProductDimensionAttribute,
  );
};

const isGeographicDimensionAttribute = (
  attribute: ProductDimensionAttribute | GeographicDimensionAttribute,
): attribute is GeographicDimensionAttribute => {
  return GEOGRAPHIC_DIMENSION_ATTRIBUTES.includes(
    attribute as GeographicDimensionAttribute,
  );
};

type ProductDimensionResult<
  T extends
    | InSeasonAggregationItemDimension
    | PreSeasonAggregationItemDimension,
> =
  | Extract<T, { name: ProductDimensionAttribute }>
  | { name: 'all_products'; model: null };

type GeographicDimensionResult<
  T extends
    | InSeasonAggregationItemDimension
    | PreSeasonAggregationItemDimension,
> =
  | Extract<T, { name: GeographicDimensionAttribute }>
  | { name: 'all_locations'; model: null };

export const getDimensions = <
  T extends
    | InSeasonAggregationItemDimension
    | PreSeasonAggregationItemDimension,
>(
  dimensions: T[],
): [ProductDimensionResult<T>, GeographicDimensionResult<T>] => {
  const productDimension = dimensions.find((dimension) =>
    isProductDimensionAttribute(dimension.name),
  ) as Extract<T, { name: ProductDimensionAttribute }> | undefined;

  const geographicDimension = dimensions.find((dimension) =>
    isGeographicDimensionAttribute(dimension.name),
  ) as Extract<T, { name: GeographicDimensionAttribute }> | undefined;

  return [
    productDimension ?? { name: 'all_products', model: null },
    geographicDimension ?? { name: 'all_locations', model: null },
  ];
};
